(() => {
  const setButtonVisible = (button, visible) => {
    button.classList.toggle('is-hidden', !visible);
    button.setAttribute('aria-hidden', String(!visible));
    button.tabIndex = visible ? 0 : -1;
  };

  const updateButtons = (container) => {
    const wrapper = container.closest('.card__swatches-scroll');
    if (!wrapper) return;

    const next = wrapper.querySelector('.card__swatches-button--next');
    let maxScroll = container.scrollWidth - container.clientWidth;
    container.classList.toggle('is-overflowing', maxScroll > 1);

    // Alignment can change the scrollable width, so measure again after
    // overflowing rows have been pinned to their true left edge.
    maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
    const atEnd = maxScroll - container.scrollLeft <= 1;

    setButtonVisible(next, maxScroll > 1 && !atEnd);
  };

  const resizeObserver = new ResizeObserver((entries) => {
    entries.forEach(({ target }) => {
      const container = target.querySelector('.card__swatches-container');
      if (container) updateButtons(container);
    });
  });

  const initContainers = (root = document) => {
    const containers = root.matches?.('.card__swatches-container')
      ? [root]
      : root.querySelectorAll('.card__swatches-container');

    containers.forEach((container) => {
      if (container.dataset.scrollButtonsReady === 'true') return;

      container.dataset.scrollButtonsReady = 'true';
      container.classList.add('is-initializing');
      container.scrollLeft = 0;
      container.addEventListener(
        'scroll',
        () => {
          updateButtons(container);
          const tooltip = container.closest('.card__swatches-scroll')?.querySelector('.card__swatches-tooltip');
          if (tooltip) tooltip.hidden = true;
        },
        { passive: true }
      );

      let isMouseDragging = false;
      let hasMouseDragged = false;
      let dragStartX = 0;
      let dragStartScrollLeft = 0;

      container.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || container.scrollWidth <= container.clientWidth) {
          return;
        }

        isMouseDragging = true;
        hasMouseDragged = false;
        dragStartX = event.clientX;
        dragStartScrollLeft = container.scrollLeft;
      });

      container.addEventListener('pointermove', (event) => {
        if (!isMouseDragging || event.pointerType !== 'mouse') return;

        const distance = event.clientX - dragStartX;
        if (Math.abs(distance) < 4) return;

        if (!hasMouseDragged) {
          hasMouseDragged = true;
          container.setPointerCapture(event.pointerId);
          container.classList.add('is-dragging');
        }
        event.preventDefault();
        container.scrollLeft = dragStartScrollLeft - distance;
      });

      const stopMouseDrag = (event) => {
        if (!isMouseDragging || event.pointerType !== 'mouse') return;

        isMouseDragging = false;
        container.classList.remove('is-dragging');
        if (container.hasPointerCapture(event.pointerId)) container.releasePointerCapture(event.pointerId);
        if (event.type === 'pointercancel') hasMouseDragged = false;
      };

      container.addEventListener('pointerup', stopMouseDrag);
      container.addEventListener('pointercancel', stopMouseDrag);
      container.addEventListener('pointerleave', () => {
        if (isMouseDragging && !hasMouseDragged) isMouseDragging = false;
      });
      container.addEventListener('dragstart', (event) => event.preventDefault());
      container.addEventListener(
        'click',
        (event) => {
          if (!hasMouseDragged) return;

          event.preventDefault();
          event.stopPropagation();
          hasMouseDragged = false;
        },
        true
      );

      resizeObserver.observe(container.closest('.card__swatches-scroll'));
      updateButtons(container);
      requestAnimationFrame(() => {
        container.scrollLeft = 0;
        container.classList.remove('is-initializing');
        updateButtons(container);
      });
    });
  };

  initContainers();
  document.addEventListener('shopify:section:load', (event) => initContainers(event.target));

  const productGridObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('.card__swatches-container') || node.querySelector('.card__swatches-container')) {
          initContainers(node);
        }
      });
    });
  });

  productGridObserver.observe(document.getElementById('MainContent') || document.body, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.card__swatches-button');
    if (!button) return;

    const container = button.closest('.card__swatches-scroll').querySelector('.card__swatches-container');
    const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
    const scrollAmount = Math.max(container.clientWidth - 20, 80);
    const targetScroll = Math.min(maxScroll, container.scrollLeft + scrollAmount);

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  });

  const showTooltip = (swatch) => {
    const wrapper = swatch.closest('.card__swatches-scroll');
    const tooltip = wrapper?.querySelector('.card__swatches-tooltip');
    const label = swatch.querySelector('.color-swatch__label');
    if (!tooltip || !label) return;

    tooltip.textContent = label.textContent.trim();
    tooltip.hidden = false;

    const wrapperRect = wrapper.getBoundingClientRect();
    const swatchRect = swatch.getBoundingClientRect();
    const halfTooltip = tooltip.offsetWidth / 2;
    const desiredLeft = swatchRect.left + swatchRect.width / 2 - wrapperRect.left;
    const left = Math.min(
      Math.max(desiredLeft, halfTooltip + 2),
      wrapperRect.width - halfTooltip - 2
    );

    tooltip.style.left = `${left}px`;
  };

  const hideTooltip = (swatch) => {
    const tooltip = swatch.closest('.card__swatches-scroll')?.querySelector('.card__swatches-tooltip');
    if (tooltip) tooltip.hidden = true;
  };

  document.addEventListener('mouseover', (event) => {
    const wrapper = event.target.closest('.card__swatches-scroll');
    if (wrapper && !wrapper.contains(event.relatedTarget)) {
      wrapper.closest('.card-wrapper')?.classList.add('is-swatches-hovered');
    }

    const swatch = event.target.closest('.card__swatches-container .color-swatch');
    if (!swatch || swatch.contains(event.relatedTarget)) return;
    showTooltip(swatch);
  });

  document.addEventListener('mouseout', (event) => {
    const wrapper = event.target.closest('.card__swatches-scroll');
    if (wrapper && !wrapper.contains(event.relatedTarget)) {
      wrapper.closest('.card-wrapper')?.classList.remove('is-swatches-hovered');
    }

    const swatch = event.target.closest('.card__swatches-container .color-swatch');
    if (!swatch || swatch.contains(event.relatedTarget)) return;
    hideTooltip(swatch);
  });

  // Touch devices don't fire mouseover, so mark swatch interaction via
  // touchstart. Keep the primary (variant) image visible while interacting
  // with the color swatches; tapping anywhere else clears it so the hover
  // secondary image can show again.
  document.addEventListener(
    'touchstart',
    (event) => {
      const wrapper = event.target.closest('.card__swatches-scroll');
      const cardWrapper = wrapper ? wrapper.closest('.card-wrapper') : null;

      document.querySelectorAll('.card-wrapper.is-swatches-hovered').forEach((el) => {
        if (el !== cardWrapper) el.classList.remove('is-swatches-hovered');
      });

      if (cardWrapper) cardWrapper.classList.add('is-swatches-hovered');
    },
    { passive: true }
  );

  document.addEventListener('focusin', (event) => {
    const swatch = event.target.closest('.card__swatches-container .color-swatch');
    if (swatch) showTooltip(swatch);
  });

  document.addEventListener('focusout', (event) => {
    const swatch = event.target.closest('.card__swatches-container .color-swatch');
    if (swatch) hideTooltip(swatch);
  });

  const imageUrlAtWidth = (source, width) => {
    const normalizedSource = source.startsWith('//') ? `https:${source}` : source;
    const url = new URL(normalizedSource, window.location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  };

  const updateCardForVariant = (variantSelects, variant) => {
    const card = variantSelects.closest('.card-wrapper');
    if (!card) return;

    const productPath = new URL(variantSelects.dataset.url, window.location.origin).pathname;

    card.querySelectorAll('a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (url.pathname !== productPath) return;

      url.searchParams.set('variant', variant.id);
      link.href = url.toString();
    });

    const featuredImage = variant.featured_image || variant.featured_media?.preview_image;
    const imageSource = featuredImage?.src;
    const cardImage = card.querySelector('.card__media img[id$="-image"]');

    if (!imageSource || !cardImage) return;

    cardImage.removeAttribute('srcset');
    cardImage.src = imageUrlAtWidth(imageSource, 533);
    cardImage.alt = featuredImage.alt || variant.title || '';

    if (featuredImage.width) cardImage.width = featuredImage.width;
    if (featuredImage.height) cardImage.height = featuredImage.height;
  };

  document.addEventListener('change', (event) => {
    if (!event.target.matches('.card__swatches-container input[type="radio"]')) return;

    const variantSelects = event.target.closest('card-variant-selects');
    const variantJson = variantSelects?.querySelector('script[type="application/json"]');
    if (!variantSelects || !variantJson) return;

    const selectedOptions = Array.from(variantSelects.querySelectorAll('.product-form__input')).map(
      (group) => group.querySelector('input[type="radio"]:checked, option:checked')?.value
    );

    const variants = JSON.parse(variantJson.textContent);
    const variant = variants.find((candidate) =>
      candidate.options.every((option, index) => option === selectedOptions[index])
    );

    if (variant) updateCardForVariant(variantSelects, variant);
  });
})();
